/*
 * AN45° — Free Gift With Purchase (GWP) engine
 * ---------------------------------------------------------------------------
 * Auto-adds / removes $0 "gift" products to the cart based on which product
 * COMBINATION the shopper has assembled, and drives the in-drawer nudge +
 * "tap to switch" chooser + toast. Extends the store's existing dynamic
 * cart-drawer pattern (see snippets/cart-drawer.liquid + assets/cart.js).
 *
 * RULES ARE DATA. The whole offer contract lives in the JSON rendered by
 * snippets/an45-gift-config.liquid (parsed from <script id="an45-gift-config">).
 * To change offers later: edit that snippet + push. No logic changes here.
 *
 * TWO TRANSPORTS, AND THEY ARE NOT INTERCHANGEABLE (2026-08-21):
 *   - GIFT lines  -> silentFetch(): the call is made from a fresh same-origin iframe
 *     realm, which Shopify's Web Pixels Manager has not instrumented, so an
 *     auto-awarded gift emits no product_added_to_cart and cannot inflate Meta's
 *     AddToCart (it used to fire one per gift, priced at gift_worth).
 *   - PAID lines  -> addTriggerProduct(): the ordinary window.fetch, empty props. A
 *     shopper add is a real add and MUST still be counted; empty props because the
 *     gift marker would make the drawer render a paid line as free.
 * The cart ROUTE is no longer a lever — WPM watches add.js, change.js AND update.js
 * alike. See the SILENT TRANSPORT block below and WEBSITE.md §10.
 *
 * Model (confirmed with the owner):
 *   - Offers are grouped into "tracks". Within a track only the highest matching
 *     "tier" is awarded (the ladder that upgrades as the basket grows).
 *   - Different tracks STACK (independent offers add their own gifts).
 *   - When one scarce product unit is required by two winning offers at once
 *     (e.g. a single shampoo powering both "combo+shampoo" and
 *     "shampoo+conditioner"), we AUTO-AWARD the most valuable allocation and
 *     expose the other as a non-blocking "tap to switch" choice.
 *
 * The pure allocation (computeGiftPlan) is exported for Node tests; the DOM /
 * cart-AJAX glue only runs in the browser (guarded by `typeof document`).
 */
(function () {
  'use strict';

  /* ======================================================================
   * PURE LOGIC (no DOM / no network — unit-tested in scripts/test-gift-engine.mjs)
   * ==================================================================== */

  // Evaluate a condition tree against a { handle: qty } map.
  //   { has: 'handle' }                          -> qty(handle) > 0
  //   { count: { of: ['h1','h2'], gte: n } }     -> sum of qtys >= n
  //   { all: [ ...conds ] }                      -> AND
  //   { any: [ ...conds ] }                      -> OR
  function evalCond(cond, qty) {
    if (!cond) return false;
    var q = function (h) { return qty[h] || 0; };
    if (cond.has) return q(cond.has) > 0;
    if (cond.count) {
      var of = cond.count.of || [];
      var sum = 0;
      for (var i = 0; i < of.length; i++) sum += q(of[i]);
      return sum >= (cond.count.gte || 1);
    }
    if (cond.all) return cond.all.every(function (c) { return evalCond(c, qty); });
    if (cond.any) return cond.any.some(function (c) { return evalCond(c, qty); });
    return false;
  }

  function qtyDelta(qty, handle, delta) {
    var out = {};
    for (var k in qty) if (Object.prototype.hasOwnProperty.call(qty, k)) out[k] = qty[k];
    out[handle] = (out[handle] || 0) + delta;
    if (out[handle] < 0) out[handle] = 0;
    return out;
  }

  // Highest matching tier per track under a given qty map -> one winner per track.
  function trackWinners(offers, qty) {
    var best = {};
    for (var i = 0; i < offers.length; i++) {
      var o = offers[i];
      if (!evalCond(o.when, qty)) continue;
      if (!best[o.track] || o.tier > best[o.track].tier) best[o.track] = o;
    }
    return Object.keys(best).map(function (t) { return best[t]; });
  }

  function dedupeGifts(winners) {
    var seen = {};
    var out = [];
    for (var i = 0; i < winners.length; i++) {
      var g = winners[i].gifts || [];
      for (var j = 0; j < g.length; j++) {
        if (!seen[g[j]]) { seen[g[j]] = 1; out.push(g[j]); }
      }
    }
    return out;
  }

  function giftsWorth(handles, config) {
    var sum = 0;
    for (var i = 0; i < handles.length; i++) {
      var g = config.gifts[handles[i]];
      if (g && typeof g.worth === 'number') sum += g.worth;
    }
    return sum;
  }

  // A winning offer "depends" on handle H if removing one unit of H breaks it.
  // Two winners contend for the same scarce unit when both depend on H and the
  // cart holds fewer units of H than the number of dependents.
  function findContested(winners, qty) {
    var handles = Object.keys(qty).filter(function (h) { return (qty[h] || 0) > 0; });
    for (var i = 0; i < handles.length; i++) {
      var h = handles[i];
      var reduced = qtyDelta(qty, h, -1);
      var deps = winners.filter(function (w) { return !evalCond(w.when, reduced); });
      if (deps.length >= 2 && (qty[h] || 0) < deps.length) {
        return { handle: h, dependents: deps };
      }
    }
    return null;
  }

  /* ---------- offer POLICIES (config.policies, added 2026-09-05) ----------
   * The offers above decide what a basket EARNS. Policies decide what the cart
   * should actually HOLD, given two things the offers cannot see:
   *   - applied discount codes: a code listed in `no_gift_codes` means "this
   *     order carries no free gift" (the story reward, or any future code that is
   *     instead-of rather than on-top-of the gift). Patterns are exact codes or a
   *     prefix with a trailing `*`; entries may be a string or
   *     { match, label, min } — label/min are DISPLAY hints for the code chip only.
   *   - the shopper's own choice: with `declinable` (default true) any gift can be
   *     removed from the cart and put back; the decision persists in a cart
   *     attribute and is honoured across basket changes.
   * This is enforced HERE, before checkout, rather than through Shopify's
   * combinesWith flags — those can only ever make the code and the gift's BXGY
   * fight, which leaves a PRICED gift line in the cart (WEBSITE.md §10). Every
   * code keeps productDiscounts:true so a gift in the cart is always free; whether
   * the gift is in the cart at all is decided by this engine.
   */
  function matchCode(code, pattern) {
    var c = String(code == null ? '' : code).trim().toUpperCase();
    var p = String(pattern == null ? '' : pattern).trim().toUpperCase();
    if (!c || !p) return false;
    if (p.charAt(p.length - 1) === '*') return c.indexOf(p.slice(0, -1)) === 0;
    return c === p;
  }
  // First applied code named by a no_gift_codes policy -> { code, rule }, else null.
  // Matched on PRESENCE, not applicability: a story code below its minimum still
  // makes this a no-gift order, so the cart never flips a gift on and off as the
  // subtotal crosses the threshold.
  function suppressingCode(codes, policies) {
    var rules = (policies && policies.no_gift_codes) || [];
    for (var i = 0; i < (codes || []).length; i++) {
      for (var j = 0; j < rules.length; j++) {
        var r = rules[j];
        var rule = typeof r === 'string' ? { match: r } : (r || {});
        if (matchCode(codes[i], rule.match)) return { code: codes[i], rule: rule };
      }
    }
    return null;
  }

  /*
   * computeGiftPlan(summary, config) -> {
   *   gifts:      [giftHandle],       // what the cart SHOULD contain (deduped, after policies)
   *   earned:     [giftHandle],       // what the offers award, BEFORE policies
   *   suppressed: null | { code, rule },   // a no-gift code is on the cart
   *   declined:   [giftHandle],       // earned gifts the shopper removed (restorable)
   *   winners:    [offer],            // the awarded offers
   *   chooser:    null | { chosenKeepId, handle, options:[{keepId,gifts,worth}] },
   *   nudges:     [{ offerId, track, tier, addHandle, text, worth }]
   * }
   * summary = { qty: {handle:qty}, choiceKeepId: string|null,
   *             codes: [appliedCode] (optional), declined: [giftHandle] (optional) }
   * Callers that pass only qty/choiceKeepId (the PDP offer line, the ladder, the
   * card generator) get gifts === earned, exactly as before policies existed.
   */
  function computeGiftPlan(summary, config, skipNudges) {
    // `skipNudges` exists only so computeNudges can ask this function what a basket
    // WOULD earn without recursing forever. Callers never pass it.
    var offers = config.offers || [];
    var qty = summary.qty || {};
    var winners = trackWinners(offers, qty);
    var contested = findContested(winners, qty);

    var chooser = null;
    var chosenWinners = winners;

    if (contested) {
      // Build one candidate allocation per dependent that KEEPS the scarce unit.
      // (Assumes the shortfall is one unit — true for the current offer set; a
      // deeper shortage still yields safe, if conservative, candidates.)
      var candidates = contested.dependents.map(function (keep) {
        var reduced = qtyDelta(qty, contested.handle, -1); // the unit is spent by `keep`
        var kept = [];
        for (var i = 0; i < winners.length; i++) {
          var w = winners[i];
          if (w === keep || contested.dependents.indexOf(w) < 0) {
            kept.push(w); // `keep`, or a winner that isn't fighting for this unit
          } else {
            // dropped dependent -> best offer its track can still earn WITHOUT the unit
            var fb = trackWinners(offers.filter(function (o) { return o.track === w.track; }), reduced)[0];
            if (fb) kept.push(fb);
          }
        }
        var g = dedupeGifts(kept);
        return { keepId: keep.id, winners: kept, gifts: g, worth: giftsWorth(g, config) };
      });
      candidates.sort(function (a, b) { return b.worth - a.worth; });

      // Drop STRICTLY DOMINATED candidates — ones whose gifts are all already in the
      // best candidate's set. Switching to such an option cannot gain the shopper
      // anything; it can only TAKE a gift away, so it is not a choice, it is a trap.
      // Sorting by worth first guarantees candidates[0] is never itself dominated
      // (a superset always outranks its subset, gift worths being positive).
      // Reachable since 2026-09-05: while the Stanley tumbler is out of stock hair-1
      // awards pouch+tote, a strict SUBSET of sun-3's tumbler+pouch+tote, so a
      // "Duo + shampoo + conditioner" cart would otherwise be invited to swap ₹1,147
      // of gifts for ₹648. Restoring the tumbler makes the sets differ again and the
      // real choice comes back on its own — this filter simply stops applying.
      var bestGifts = candidates[0].gifts;
      candidates = candidates.filter(function (c, i) {
        if (i === 0) return true;
        return !c.gifts.every(function (h) { return bestGifts.indexOf(h) >= 0; });
      });

      var chosen = null;
      if (summary.choiceKeepId) {
        chosen = candidates.filter(function (c) { return c.keepId === summary.choiceKeepId; })[0] || null;
      }
      if (!chosen) chosen = candidates[0];
      chosenWinners = chosen.winners;
      // With every alternative dominated there is nothing to choose between: leave
      // `chooser` null so the drawer renders no switcher at all.
      if (candidates.length > 1) {
        chooser = {
          chosenKeepId: chosen.keepId,
          handle: contested.handle,
          options: candidates.map(function (c) { return { keepId: c.keepId, gifts: c.gifts, worth: c.worth }; })
        };
      }
    }

    // ---- policies: from what is EARNED to what the cart should HOLD ----
    var earned = dedupeGifts(chosenWinners);
    var policies = config.policies || {};
    var suppressed = suppressingCode(summary.codes, policies);
    var declinedIn = policies.declinable === false ? [] : (summary.declined || []);

    /* 🚨 A DECLINE TAKES THE WHOLE OFFER'S GIFT SET, NEVER ONE ROW OF IT (2026-09-09).
     *
     * This is a money rule, not a UX preference. Gifts are zeroed by automatic BXGY
     * discounts, and a BXGY's `customerGets.discountOnQuantity.quantity = N` is
     * ALL-OR-NOTHING: Shopify does not apply it at all unless N entitled units are in
     * the cart. (Verified on the live store 2026-09-09 with probe-gift-pricing.mjs —
     * two sunscreens, decline the mini shampoo, and the mini conditioner came back
     * CHARGED at ₹149 on a line still badged FREE GIFT. `percentage` is not an escape
     * hatch: Shopify rejects it with "Only discountOnQuantity permitted with bxgy
     * discounts.")
     *
     * One purchased unit often has to fund several gifts — a single sunscreen funds BOTH
     * miniatures — so those gifts MUST share one N-unit rule, and removing one of them
     * necessarily breaks it. Declining the offer's whole set instead always leaves every
     * rule either fully satisfied or completely idle, which is the only safe state.
     *
     * The cart ATTRIBUTE still stores just the handle the shopper clicked; the group is
     * expanded here, at compute time, from the winning offers. That keeps the attribute
     * small and lets the group follow the basket (the same mini declined on a Duo cart
     * belongs to a 3-gift rule, so the tote goes with it).
     */
    var giftGroups = {};
    earned.forEach(function (h) {
      var group = [];
      chosenWinners.forEach(function (w) {
        if ((w.gifts || []).indexOf(h) < 0) return;
        (w.gifts || []).forEach(function (g) {
          if (earned.indexOf(g) >= 0 && group.indexOf(g) < 0) group.push(g);
        });
      });
      giftGroups[h] = group.length ? group : [h];
    });
    var declined = [];
    earned.forEach(function (h) {
      if (declinedIn.indexOf(h) < 0) return;
      giftGroups[h].forEach(function (g) { if (declined.indexOf(g) < 0) declined.push(g); });
    });
    var gifts = suppressed ? [] : earned.filter(function (h) { return declined.indexOf(h) < 0; });

    return {
      gifts: gifts,
      earned: earned,
      suppressed: suppressed,
      declined: suppressed ? [] : declined,
      // handle -> every gift that must leave the cart with it. The click handler uses
      // this so "Add back" restores the same set the decline removed.
      giftGroups: giftGroups,
      winners: chosenWinners,
      // No switcher and no "add X to unlock Y" while the order carries no gift —
      // inviting a shopper to buy more for a gift this code excludes is a trap.
      chooser: suppressed ? null : chooser,
      nudges: suppressed || skipNudges ? [] : computeNudges(offers, qty, config)
    };
  }

  // "One product away" opportunities: for each unsatisfied offer, is there a
  // single product whose addition flips it on? Keep the nearest step per track,
  // then surface the best 1-2 by gift value.
  function computeNudges(offers, qty, config) {
    // Current earned tier per track, so we only ever nudge an UPGRADE (never a
    // lower tier that also happens to be "one product away").
    var wonTier = {};
    var cur = trackWinners(offers, qty);
    for (var w = 0; w < cur.length; w++) wonTier[cur[w].track] = cur[w].tier;

    var found = [];
    for (var i = 0; i < offers.length; i++) {
      var o = offers[i];
      if (evalCond(o.when, qty)) continue;
      if (o.tier <= (wonTier[o.track] || 0)) continue; // already at/above this tier in the track
      var tryList = (o.nudge && o.nudge.try) || [];
      var addHandle = null;
      for (var t = 0; t < tryList.length; t++) {
        if (evalCond(o.when, qtyDelta(qty, tryList[t], 1))) { addHandle = tryList[t]; break; }
      }
      if (!addHandle) continue;
      found.push({
        offerId: o.id, track: o.track, tier: o.tier,
        addHandle: addHandle,
        text: (o.nudge && o.nudge.text) || '',
        worth: giftsWorth(o.gifts || [], config)
      });
    }
    var byTrack = {};
    for (var k = 0; k < found.length; k++) {
      var n = found[k];
      if (!byTrack[n.track] || n.tier < byTrack[n.track].tier) byTrack[n.track] = n; // nearest step
    }
    var list = Object.keys(byTrack).map(function (t) { return byTrack[t]; });

    /* ⚠️ A NUDGE MUST NOT PROMISE A GIFT THE ENGINE WOULD NOT AWARD (2026-09-09).
     *
     * "One product away" per TRACK is not enough, because tracks are not independent:
     * two offers can both be one product away, both name the SAME product, and then
     * CONTEND for it once it arrives — so only one of them ever pays out. A cart with
     * two sunscreens and the shampoo showed both "Add the Conditioner to upgrade to a
     * free tote + insulated sipper" (sun-3, which wins) and "Add the Conditioner for a
     * free frosted bottle + mini sunscreen duo" (hair-1, which loses the contest and
     * pays nothing). Two buttons, same product, and one of them lying.
     *
     * So each surviving nudge is re-checked against the REAL allocation for the basket
     * it would create: keep it only if its offer is actually among the winners. Same
     * invariant the PDP buy-box line holds (test-pdp-offer.mjs).
     */
    list = list.filter(function (n) {
      var after = computeGiftPlan({ qty: qtyDelta(qty, n.addHandle, 1) }, config, true);
      return (after.winners || []).some(function (w) { return w.id === n.offerId; });
    });

    // One button per PRODUCT. If two truthful nudges still ask for the same add, the
    // richer one speaks for both — two rows saying "add the conditioner" is noise.
    var byAdd = {};
    list.forEach(function (n) {
      if (!byAdd[n.addHandle] || n.worth > byAdd[n.addHandle].worth) byAdd[n.addHandle] = n;
    });

    return Object.keys(byAdd).map(function (h) { return byAdd[h]; })
      .sort(function (a, b) { return b.worth - a.worth; })
      .slice(0, 2);
  }

  /*
   * planCartOps(cart, desiredIds, giftVariantIds) -> { removals: {lineKey:qty}, adds: [{id,quantity}] }
   *
   * The MINIMAL set of cart mutations that makes the cart's gift lines match the
   * plan, batched so the whole reconciliation is at most two Ajax calls instead of
   * one per gift.
   *
   * ⚠️ SAFETY INVARIANTS (asserted in scripts/test-gift-turbo.mjs — read
   * WEBSITE.md §10 "Gift-exploit hardening" before touching this):
   *   1. Every op targets a line whose variant id is a KNOWN GIFT. A customer's own
   *      product line can never appear in `removals`, whatever the cart looks like.
   *   2. At most ONE line per gift variant survives at quantity 1. Duplicate gift
   *      lines (same variant on two lines, e.g. differing properties) are zeroed —
   *      a second copy would be a gift the BXGY rules do not free.
   *   3. Quantities are only ever set to 0 or 1. This function cannot increase the
   *      quantity of anything.
   * It decides nothing about WHICH gifts are earned — that is computeGiftPlan's job,
   * unchanged. This only translates a plan into calls.
   */
  function planCartOps(cart, desiredIds, giftVariantIds) {
    var removals = {};
    var adds = [];
    var kept = {};
    var items = (cart && cart.items) || [];

    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!giftVariantIds[it.id]) continue; // invariant 1: never touch a non-gift line
      var want = 0;
      if (desiredIds[it.id] && !kept[it.id]) { want = 1; kept[it.id] = true; } // invariant 2
      if (it.quantity !== want) removals[it.key] = want; // invariant 3
    }

    Object.keys(desiredIds).forEach(function (id) {
      if (!kept[id]) adds.push({ id: parseInt(id, 10), quantity: 1 });
    });

    return { removals: removals, adds: adds };
  }

  var API = {
    evalCond: evalCond,
    qtyDelta: qtyDelta,
    trackWinners: trackWinners,
    findContested: findContested,
    computeGiftPlan: computeGiftPlan,
    computeNudges: computeNudges,
    planCartOps: planCartOps,
    matchCode: matchCode,
    suppressingCode: suppressingCode
  };

  // Node export for tests.
  if (typeof module !== 'undefined' && module.exports) module.exports = API;

  // Browser export. assets/an45-offer-ladder.js (homepage rail + /pages/offers)
  // reuses evalCond/qtyDelta/trackWinners so the marketing display asks the SAME
  // predicate the cart allocates from — a rung can never claim "unlocked" unless
  // this engine would actually award it. Read-only: the ladder never mutates
  // gift lines itself, it only adds TRIGGER products and lets reconcile() run.
  if (typeof window !== 'undefined') window.AN45GiftEngine = API;

  /* ======================================================================
   * BROWSER GLUE (cart AJAX + drawer rail + toast)
   * ==================================================================== */
  if (typeof document === 'undefined') return;

  var GIFT_PROP = '_an45_gift';
  var CHOICE_ATTR = '_an45_gift_choice';
  var DECLINED_ATTR = '_an45_gift_declined'; // comma-separated gift handles the shopper removed
  var SELF = 'an45-gift-engine';
  // Sections we ask the cart Ajax calls to render inline, so the drawer can repaint
  // from the SAME response that mutated the cart (assets/cart.js onCartUpdate reads
  // event.sections['cart-drawer']) instead of firing another GET /cart?section_id=.
  var SECTIONS = ['cart-drawer'];
  // Clean inline gift icon (brand rule: SVG, never emoji). Colour via currentColor.
  var GIFT_SVG = '<svg class="an45-gift-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
  var TAG_SVG = '<svg class="an45-gift-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';

  /* ======================================================================
   * SILENT TRANSPORT — cart calls the Web Pixels Manager cannot see
   * ----------------------------------------------------------------------
   * ⚠️ ANALYTICS-CRITICAL. Read this before changing any gift cart call.
   *
   * WPM instruments the Ajax cart by monkey-patching `window.fetch` and
   * `XMLHttpRequest.prototype.{open,send}` ON THE TOP WINDOW, then emitting one
   * `product_added_to_cart` per line the RESPONSE says grew:
   *     /cart/add.js     -> response `items`
   *     /cart/change.js  -> response `items_added`
   *     /cart/update.js  -> response `items_changelog.added`
   * The Facebook & Instagram app maps that event to a Meta **AddToCart** (browser
   * + CAPI); the Google and Klaviyo pixels map it too.
   *
   * The 2026-08-12 fix moved gift adds add.js -> update.js because update.js was
   * not instrumented then. Shopify has since shipped `items_changelog` on the Ajax
   * cart plus the handler that reads it, so update.js began firing exactly like
   * add.js and every auto-awarded gift logged its own AddToCart again — a click
   * that earns two gifts reported three, with gift variants in `content_ids` and
   * `gift_worth` in `value`. Verified 2026-08-21 against the live WPM bundle and a
   * live POST /cart/update.js, which does return `items_changelog.added`.
   *
   * So the ROUTE is no longer a lever; every mutating cart route is watched. The
   * TRANSPORT is. WPM patches the top window's realm only and does not touch
   * frames, so `fetch` taken from a fresh same-origin `about:blank` iframe is the
   * untouched native function. Same origin = same cookies = same cart; the only
   * difference is that nothing is listening.
   *
   * Guards, because a silent failure here is a wrong number nobody notices:
   *   - The candidate must stringify as `[native code]`. If a future WPM patches
   *     child realms too, that check fails and we fall back LOUDLY rather than
   *     believing we are silent when we are not.
   *   - Absolute URLs only: an `about:blank` document has no useful base URL, so a
   *     relative path would not resolve.
   *   - The iframe must STAY in the DOM. Detaching it tears down the realm and
   *     aborts every request in flight through it.
   *   - Any failure falls back to the instrumented `window.fetch`. A correct cart
   *     beats a clean number — the same trade as the add.js fallback in postAdd().
   *
   * NEVER route a shopper's own add through this. See addTriggerProduct().
   * ==================================================================== */
  var silentFrame = null, silentWin = null, silentOff = false;

  function isNative(fn) {
    try { return /\{\s*\[native code\]\s*\}/.test(Function.prototype.toString.call(fn)); }
    catch (e) { return false; }
  }

  function silentRealm() {
    if (silentOff) return null;
    if (silentWin && silentFrame && silentFrame.isConnected && silentFrame.contentWindow === silentWin) {
      return silentWin;
    }
    silentWin = null;
    try {
      var host = document.body || document.documentElement;
      if (!host) return null; // called before <body> exists — try again next time
      var frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');   // it is not content; keep it out of the a11y tree
      frame.setAttribute('tabindex', '-1');        // …and out of the tab order
      frame.setAttribute('title', 'AN45 cart helper');
      // NO src. A src-less iframe keeps the initial `about:blank` document, which is
      // same-origin and available synchronously. Setting src="about:blank" navigates it
      // instead, which can swap contentWindow out from under a cached reference.
      frame.style.cssText = 'position:absolute;left:-9999px;top:0;width:0;height:0;border:0;visibility:hidden';
      host.appendChild(frame);
      var win = frame.contentWindow;
      if (win && typeof win.fetch === 'function' && isNative(win.fetch)) {
        silentFrame = frame;
        silentWin = win;
        return silentWin;
      }
      host.removeChild(frame);
    } catch (e) {
      console.warn('[an45-gift] silent transport unavailable', e);
    }
    silentOff = true; // stop retrying; every gift call now costs a phantom AddToCart
    console.warn('[an45-gift] no un-instrumented fetch — gift cart calls will emit AddToCart');
    return null;
  }

  // Same origin, so cookies/cart are identical; `about:blank` has no base URL, hence absolute.
  function absUrl(path) {
    var p = String(path == null ? '' : path);
    if (/^https?:/i.test(p)) return p;
    return window.location.origin + (p.charAt(0) === '/' ? p : '/' + p);
  }

  // GIFT cart mutations only.
  function silentFetch(url, opts) {
    var win = silentRealm();
    var target = absUrl(url);
    if (win) {
      try { return win.fetch.call(win, target, opts); }
      catch (e) { console.warn('[an45-gift] silent fetch failed; retrying instrumented', e); }
    }
    return fetch(target, opts);
  }

  function initGiftEngine() {
    if (window.Shopify && window.Shopify.designMode) return; // don't mutate carts in the theme editor

    var cfgEl = document.getElementById('an45-gift-config');
    if (!cfgEl) return;
    var config;
    try { config = JSON.parse(cfgEl.textContent); } catch (e) { console.warn('[an45-gift] bad config JSON', e); return; }
    if (!config.offers || !config.offers.length) return;

    // ⚡ TURBO — the low-latency path (added 2026-08-05). `false` restores the original
    // fetch-then-one-call-per-gift-then-refetch behaviour EXACTLY, including in cart.js
    // (which falls back to its own GET when no sections ride along) and skips all
    // optimistic painting. THIS IS THE ROLLBACK SWITCH: flip it in
    // snippets/an45-gift-config.liquid, or run `node scripts/push-gift-turbo.mjs --off`.
    var TURBO = config.turbo !== false;

    // Policies (2026-09-05): the gift rows' "Remove" buttons are server-rendered so they
    // never flash in late; this class hides them when the config says gifts are locked.
    document.documentElement.classList.toggle('an45-gifts-locked', !!(config.policies && config.policies.declinable === false));

    var giftVariantIds = {};
    Object.keys(config.gifts || {}).forEach(function (h) {
      var g = config.gifts[h];
      if (g && g.id) giftVariantIds[g.id] = h;
    });
    var routes = window.routes || {};

    var reconciling = false;
    var pending = false;
    var lastPlan = null;
    var lastRailHTML = null; // the HTML we last WROTE into the rail. Compare against THIS, never
                             // rail.innerHTML (the browser normalizes it → never equal → the
                             // MutationObserver repaints every frame → buttons unclickable + jank).
    var prevGifts = null; // last desired gift-handle set (for toast); null = first run
    var lastQty = null;   // qty map from the last AUTHORITATIVE cart — the base the optimistic
                          // guess extrapolates from. Never used to decide a mutation.
    var lastChoice = null;
    var lastCodes = [];      // applied discount codes from the last authoritative cart
    var lastDeclined = [];   // gift handles the shopper removed (cart attribute)
    var lastCart = null;     // the last authoritative cart itself — the code chips render from it
    var lastCodesHTML = null; // same string-compare guard as lastRailHTML, for #an45-cart-codes
    var codesOpen = false;    // panel state + half-typed code, kept HERE because cart.js replaces
    var codesTyped = '';      // the whole drawer inner on every update and would lose them

    function fetchCart() {
      return fetch((routes.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); });
    }
    // A response we can safely treat as the WHOLE cart. /cart/change.js and /cart/update.js
    // return this shape; /cart/add.js returns only the lines it added, which would read as
    // an almost-empty cart and strip every earned gift — hence the strict shape check.
    function isFullCart(c) {
      return !!c && typeof c.item_count === 'number' && Array.isArray(c.items) && typeof c.token === 'string';
    }
    function withSections(body, want) {
      if (want) {
        body.sections = SECTIONS;
        body.sections_url = window.location.pathname;
      }
      return body;
    }
    // Batched removal. `updates` is keyed by LINE ITEM KEY, which /cart/update.js accepts
    // alongside variant ids — keys are used because two gift lines can share a variant.
    //
    // ⚠️ Verified, not assumed. A silent no-op here would strand an unearned gift in the
    // cart, and unearned gifts are PRICED (WEBSITE.md §10 — they are only zeroed by the
    // automatic BXGY discounts in a qualifying cart), so the shopper would be charged for
    // something the drawer had been calling free. If the response doesn't show the
    // quantities we asked for, fall back to the old one-line-at-a-time /cart/change.js.
    function postUpdate(updates, wantSections) {
      return silentFetch(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(withSections({ updates: updates }, wantSections))
      }).then(function (r) { return r.json(); }).then(function (cart) {
        if (!isFullCart(cart)) return cart;
        var stale = Object.keys(updates).filter(function (key) {
          var found = (cart.items || []).filter(function (it) { return it.key === key; })[0];
          return (found ? found.quantity : 0) !== updates[key];
        });
        if (!stale.length) return cart;

        console.warn('[an45-gift] /cart/update.js by line key did not apply; using /cart/change.js', stale);
        return stale.reduce(function (p, key) {
          return p.then(function () { return changeLine(key, updates[key]); });
        }, Promise.resolve()).then(function () {
          return { sections: null }; // no sections to hand on — cart.js re-fetches, as it always did
        });
      });
    }
    // ⚠️ ANALYTICS-CRITICAL: gift lines are created through silentFetch(), never through
    // the instrumented window.fetch. See the SILENT TRANSPORT block above for why the
    // route (/cart/update.js vs /cart/add.js) stopped being enough on its own.
    //
    // /cart/update.js keyed by VARIANT ID is still the route of choice because it creates
    // the line in ONE batched call. Its one cost: update.js cannot set line properties, so
    // gift lines do not carry `_an45_gift`. That property was never load-bearing — the
    // engine matches gift lines by `giftVariantIds`, and cart-drawer.liquid /
    // main-cart-items.liquid match the `an45-gift` product TAG first (the property is only
    // an `or` fallback). Keep that tag on every gift product or the drawer stops labelling
    // them free.
    //
    // If the update doesn't apply we fall back to add.js — also silent, and still verified:
    // a correct cart beats clean analytics, because an unearned/missing gift is money.
    function postAdd(items, wantSections) {
      var updates = {};
      items.forEach(function (it) { updates[it.id] = it.quantity; });
      return silentFetch(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(withSections({ updates: updates }, wantSections))
      }).then(function (r) { return r.json(); }).then(function (cart) {
        if (!isFullCart(cart)) {
          console.warn('[an45-gift] /cart/update.js add did not return a cart; using /cart/add.js');
          return addViaAddJs(items, wantSections);
        }
        var stale = items.filter(function (it) {
          var have = 0;
          (cart.items || []).forEach(function (x) {
            if (String(x.id) === String(it.id)) have += x.quantity;
          });
          return have < it.quantity;
        });
        if (!stale.length) return cart;
        console.warn('[an45-gift] /cart/update.js did not add the gift; using /cart/add.js', stale);
        return addViaAddJs(stale, wantSections);
      });
    }
    // The original add path. For a GIFT this is postAdd()'s fallback; for a real product
    // it is the CORRECT route (see addTriggerProduct).
    // TWO defaults here, and they move together — both describe a GIFT line:
    //   `props`  defaults to the gift marker; pass {} for a paid line, or the drawer will
    //            render it as a free gift.
    //   `counted` defaults to false (silent transport); pass true for a paid line, or a
    //            genuine shopper add goes missing from Meta/Google/Klaviyo.
    function addViaAddJs(items, wantSections, props, counted) {
      var lineProps = props;
      if (lineProps === undefined) { lineProps = {}; lineProps[GIFT_PROP] = 'Free gift'; }
      var payload = items.map(function (it) {
        return { id: it.id, quantity: it.quantity, properties: lineProps };
      });
      var url = routes.cart_add_url || '/cart/add.js';
      var req = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(withSections({ items: payload }, wantSections))
      };
      // Call each transport DIRECTLY. `var http = counted ? fetch : silentFetch` looks
      // tidier and throws "Illegal invocation": a detached native fetch has no `this`.
      var res = counted ? fetch(url, req) : silentFetch(url, req);
      return res.then(function (r) { return r.json(); });
    }
    // GIFT line, legacy path only (TURBO off) — silent, and WPM reads change.js's
    // `items_added`, so this must not go through the instrumented fetch either.
    function changeLine(key, quantity) {
      return silentFetch(routes.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
    }
    function addLine(id) { // GIFT line, legacy path only (TURBO off) — silent, like postAdd
      return postAdd([{ id: parseInt(id, 10), quantity: 1 }], false);
    }

    // The nudge's "Add" button adds a REAL, PAID product (a second sunscreen, a
    // conditioner…) to unlock a tier — not a gift. It deliberately uses /cart/add.js on
    // the ORDINARY instrumented fetch so it DOES emit a Meta AddToCart: that is a genuine
    // shopper add and belongs in the numbers. Only auto-awarded gift lines go silent.
    // Hence both non-default args: empty props (the gift marker would make the drawer call
    // a paid line free) and counted:true (the transport).
    // Resolves with the /cart/add.js body (added lines), which is what the optimistic
    // painter expects as `cartData`.
    function addTriggerProduct(id) {
      return addViaAddJs([{ id: parseInt(id, 10), quantity: 1 }], false, {}, true);
    }

    // Run the batched ops: removals first (one /cart/update.js), then adds (one more).
    // Both are gift-only and both go out on the silent transport. The LAST call carries
    // `sections`, so its response repaints the drawer. Resolves with that response, or
    // null when there was nothing to do.
    function runOps(ops) {
      var steps = [];
      if (Object.keys(ops.removals).length) steps.push('rm');
      if (ops.adds.length) steps.push('add');
      if (!steps.length) return Promise.resolve(null);

      var chain = Promise.resolve(null);
      steps.forEach(function (kind, i) {
        var last = i === steps.length - 1;
        chain = chain.then(function () {
          return kind === 'rm' ? postUpdate(ops.removals, last) : postAdd(ops.adds, last);
        });
      });
      return chain;
    }
    // Attributes only — no line ever changes, so WPM has nothing to report. Silent anyway,
    // so that every gift-engine cart call shares one transport and none can drift back.
    // Resolves with the full cart /cart/update.js returns, so callers can reconcile from
    // it without another GET.
    function setAttrs(attrs) {
      return silentFetch(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) { return r.json(); });
    }
    function setChoiceAttr(value) {
      var attrs = {}; attrs[CHOICE_ATTR] = value || '';
      return setAttrs(attrs);
    }
    function setDeclined(handles) {
      var attrs = {}; attrs[DECLINED_ATTR] = (handles || []).join(',');
      return setAttrs(attrs);
    }
    // Discount codes are a SHOPPER action: ordinary fetch, no line changes, nothing for
    // WPM to count. `codes` is the complete list to keep — Shopify replaces, not merges —
    // so removing one code means sending the others. An empty list clears them all.
    function setCodes(codes, wantSections) {
      return fetch(absUrl(routes.cart_update_url || '/cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(withSections({ discount: (codes || []).join(',') }, wantSections))
      }).then(function (r) { return r.json(); });
    }

    // Codes ON the cart, applicable or not. /cart.js exposes `discount_codes` (code +
    // applicable); older responses only show APPLIED codes through their applications,
    // which is the fallback — it can miss a code below its minimum, never invent one.
    function cartCodes(cart) {
      var out = [];
      var dc = cart && cart.discount_codes;
      if (Array.isArray(dc)) {
        dc.forEach(function (d) { if (d && d.code) out.push(String(d.code)); });
        return out;
      }
      var apps = ((cart && cart.cart_level_discount_applications) || []).slice();
      ((cart && cart.items) || []).forEach(function (it) {
        (it.line_level_discount_allocations || []).forEach(function (a) { if (a && a.discount_application) apps.push(a.discount_application); });
      });
      apps.forEach(function (a) {
        if (a && a.type === 'discount_code' && a.title && out.indexOf(String(a.title)) < 0) out.push(String(a.title));
      });
      return out;
    }
    function parseDeclined(cart) {
      var raw = (cart && cart.attributes && cart.attributes[DECLINED_ATTR]) || '';
      return String(raw).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }

    function buildSummary(cart) {
      var qty = {};
      (cart.items || []).forEach(function (it) {
        if (giftVariantIds[it.id]) return; // ignore gift lines when reading the basket
        qty[it.handle] = (qty[it.handle] || 0) + it.quantity;
      });
      var choice = (cart.attributes && cart.attributes[CHOICE_ATTR]) || null;
      return { qty: qty, choiceKeepId: choice, codes: cartCodes(cart), declined: parseDeclined(cart) };
    }

    // Reconcile the cart's gift lines to match the computed plan, then paint the rail.
    //
    // `hintCart` is a cart the publisher already had in hand (cart.js publishes the
    // full /cart/change.js response as `cartData`). Reusing it saves a GET /cart.js.
    // It is used ONLY when it passes isFullCart() — anything else, we fetch. The
    // decision of what the cart should contain is therefore always made from a
    // complete, server-authored cart, exactly as before.
    function reconcile(hintCart) {
      if (reconciling) { pending = true; return; }
      reconciling = true;

      var usable = TURBO && isFullCart(hintCart);

      (usable ? Promise.resolve(hintCart) : fetchCart()).then(function (cart) {
        var summary = buildSummary(cart);
        lastQty = summary.qty;
        lastChoice = summary.choiceKeepId;
        lastCodes = summary.codes;
        lastDeclined = summary.declined;
        lastCart = cart;

        var plan = computeGiftPlan(summary, config);
        lastPlan = plan;

        /* Prune declines the basket no longer earns (2026-09-09).
         *
         * `_an45_gift_declined` is a cart ATTRIBUTE, and attributes survive
         * /cart/clear.js — the items go, the attribute stays. So a shopper who removed
         * the tote, emptied the cart and started again got no tote and a stale
         * "Removed: … Add back" prompt, with nothing on screen explaining why (owner,
         * 2026-09-09). computeGiftPlan already IGNORES a decline for a gift the basket
         * does not earn, so this only tidies the attribute — but tidying it is what
         * stops the decline waking back up when the gift is re-earned later.
         *
         * A decline for a gift that IS still earned stays put, which is what keeps it
         * surviving a tier upgrade (test-gift-engine covers both).
         */
        if (lastDeclined && lastDeclined.length) {
          var stillEarned = lastDeclined.filter(function (h) { return (plan.earned || []).indexOf(h) >= 0; });
          if (stillEarned.length !== lastDeclined.length) {
            lastDeclined = stillEarned;
            setDeclined(stillEarned).catch(function () {});
          }
        }

        var desired = {};
        plan.gifts.forEach(function (h) {
          var g = config.gifts[h];
          if (g && g.id) desired[g.id] = true;
        });

        if (!TURBO) {
          // ---- legacy path: one call per gift, then a blind cartUpdate ----
          var giftLines = (cart.items || []).filter(function (it) { return giftVariantIds[it.id]; });
          var ops = [];
          giftLines.forEach(function (it) {
            var want = desired[it.id] ? 1 : 0;
            if (it.quantity !== want) ops.push(function () { return changeLine(it.key, want); });
          });
          Object.keys(desired).forEach(function (id) {
            var already = giftLines.some(function (it) { return String(it.id) === String(id) && it.quantity >= 1; });
            if (!already) ops.push(function () { return addLine(id); });
          });
          var chain = ops.reduce(function (p, op) { return p.then(op); }, Promise.resolve());
          return chain.then(function () {
            maybeToast(plan);
            prevGifts = plan.gifts.slice();
            if (ops.length) return publish(PUB_SUB_EVENTS.cartUpdate, { source: SELF });
          });
        }

        // ---- turbo path: <=2 batched calls, drawer repainted from the last response ----
        var batched = planCartOps(cart, desired, giftVariantIds);
        return runOps(batched).then(function (res) {
          maybeToast(plan);
          prevGifts = plan.gifts.slice();
          if (!res) return; // cart already correct — leave the DOM alone
          return publish(PUB_SUB_EVENTS.cartUpdate, { source: SELF, sections: res.sections || null });
        });
      }).catch(function (e) {
        console.warn('[an45-gift] reconcile failed', e);
      }).finally(function () {
        reconciling = false;
        // Unconditional: by now the authoritative state is on screen, so ANY optimistic
        // node still standing is stale by definition. The usual case is a no-op (the
        // repaint already replaced <cart-drawer-items> wholesale); it also covers the
        // no-ops-needed and request-failed cases, where nothing repainted at all.
        // A guess must never outlive the truth.
        clearOptimistic();
        paintRail();
        if (pending) { pending = false; reconcile(); }
      });
    }

    /* ======================================================================
     * OPTIMISTIC PAINT (turbo only)
     * ----------------------------------------------------------------------
     * The plan is computable locally, so the drawer can show the gift the moment
     * the shopper clicks instead of after the round trips. Hard rules:
     *
     *   • PAINT ONLY. Nothing here calls /cart/*. Optimistic state never reaches
     *     reconcile(), which always decides from a server-authored cart. It cannot
     *     put a gift in the cart, so it cannot be exploited to obtain one.
     *   • NO `name="updates[]"` in the injected row. The drawer form's updates[]
     *     inputs are POSITIONAL (see snippets/cart-drawer.liquid) — an extra one
     *     would misapply quantities to every line after it if the shopper hit
     *     Checkout inside the ~300ms window.
     *   • It always loses to the server. The authoritative repaint replaces the
     *     whole <cart-drawer-items>; clearOptimistic() + a watchdog cover the
     *     no-op and failure cases.
     * ==================================================================== */

    var optimisticTimer = null;
    var optimisticPlan = null; // outstanding guess; re-applied if the drawer re-renders
                               // underneath us (product-form.js publishes cartUpdate
                               // BEFORE it repaints the drawer, which would wipe the row).

    // Extrapolate the resulting basket from the last authoritative cart + whatever the
    // publisher tells us changed. Returns null when we cannot be confident — in which
    // case we simply don't paint early and the shopper sees the old (correct) timing.
    // A full summary (qty + choice + codes + declined) for the optimistic plan. A full
    // cart is exact; an add.js response only carries the added lines, so the basket is
    // extrapolated and the policy inputs are the last authoritative ones — a guess can
    // never paint a gift a no-gift code or the shopper's own decline has excluded.
    function optimisticSummary(evt) {
      if (!lastQty) return null;
      var cd = evt && evt.cartData;
      if (!cd) return null;
      if (isFullCart(cd)) return buildSummary(cd); // change.js / update.js → exact

      // /cart/add.js → only the added lines; each carries the line's RESULTING quantity.
      var added = cd.items || (cd.id ? [cd] : null);
      if (!added || !added.length) return null;
      var q = {};
      for (var k in lastQty) if (Object.prototype.hasOwnProperty.call(lastQty, k)) q[k] = lastQty[k];
      for (var i = 0; i < added.length; i++) {
        var it = added[i];
        if (!it || !it.handle || giftVariantIds[it.id]) continue;
        // If the handle also has another variant in the cart this undercounts, which can
        // only predict FEWER gifts — never a gift the shopper hasn't earned.
        q[it.handle] = it.quantity;
      }
      return { qty: q, choiceKeepId: lastChoice, codes: lastCodes, declined: lastDeclined };
    }

    function buildOptimisticRow(handle, variantId) {
      var tpl = document.getElementById('an45-gift-row-tpl');
      if (!tpl || !tpl.content) return null;
      var g = config.gifts[handle];
      if (!g) return null;
      var frag = tpl.content.cloneNode(true);
      var tr = frag.querySelector('tr');
      if (!tr) return null;
      tr.setAttribute('data-an45-optimistic', String(variantId));
      var img = tr.querySelector('.cart-item__image');
      if (img) {
        if (g.image) { img.setAttribute('src', g.image); img.setAttribute('alt', g.title || ''); }
        else img.remove();
      }
      var name = tr.querySelector('.cart-item__name');
      if (name) name.textContent = g.title || handle;
      return tr;
    }

    function applyOptimistic(plan) {
      var tbody = document.querySelector('#CartDrawer .cart-items tbody');
      if (!tbody) return;

      var want = {};
      plan.gifts.forEach(function (h) {
        var g = config.gifts[h];
        if (g && g.id) want[String(g.id)] = h;
      });

      // 1) Gifts that are no longer earned: hide the REAL rows. No markup is built, so
      //    there is nothing to drift out of sync with cart-drawer.liquid.
      var present = {};
      Array.prototype.forEach.call(tbody.querySelectorAll('tr.an45-gift-line'), function (tr) {
        var vid = tr.getAttribute('data-an45-gift-id');
        if (!vid) return;
        if (want[vid]) { present[vid] = true; tr.classList.remove('an45-gift-line--pending-remove'); }
        else tr.classList.add('an45-gift-line--pending-remove');
      });

      // 2) Gifts about to be earned: draw a stand-in row (CSS `order: 50` keeps it below
      //    the shopper's own products, same as a real one).
      Object.keys(want).forEach(function (vid) {
        if (present[vid]) return;
        if (tbody.querySelector('[data-an45-optimistic="' + vid + '"]')) return;
        var tr = buildOptimisticRow(want[vid], vid);
        if (tr) tbody.appendChild(tr);
      });

      // Watchdog: if a reconcile is lost entirely (offline, tab suspended), never leave a
      // stand-in row claiming a gift the cart does not hold.
      if (optimisticTimer) clearTimeout(optimisticTimer);
      optimisticTimer = setTimeout(clearOptimistic, 8000);
    }

    function clearOptimistic() {
      optimisticPlan = null;
      if (optimisticTimer) { clearTimeout(optimisticTimer); optimisticTimer = null; }
      var root = document.getElementById('CartDrawer');
      if (!root) return;
      Array.prototype.forEach.call(root.querySelectorAll('[data-an45-optimistic]'), function (n) {
        n.parentNode && n.parentNode.removeChild(n);
      });
      Array.prototype.forEach.call(root.querySelectorAll('.an45-gift-line--pending-remove'), function (n) {
        n.classList.remove('an45-gift-line--pending-remove');
      });
    }

    function paintOptimistic(evt) {
      if (!TURBO) return;
      var s = optimisticSummary(evt);
      if (!s) return;
      try {
        var plan = computeGiftPlan(s, config);
        optimisticPlan = plan;
        applyOptimistic(plan);
        lastPlan = plan; // so the rail/nudge updates in the same frame
        paintRail();
      } catch (e) {
        console.warn('[an45-gift] optimistic paint skipped', e);
        clearOptimistic();
      }
    }

    /* ---------- rail (nudge + chooser) ---------- */
    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
    function giftName(h) { var g = config.gifts[h]; return g ? g.title : h; }
    // In-cart the "AN45° " prefix is noise — short names keep the rail one line tall.
    function shortName(h) { return String(giftName(h)).replace(/^AN45[°º]?\s*/, ''); }
    function listShort(handles) { return handles.map(shortName).join(' + '); }

    function buildRailHTML(plan) {
      if (!plan) return '';
      var html = '';

      // Non-blocking chooser, kept SMALL: one line stating the current set + a
      // collapsed "Prefer a different free gift?" switcher (<details>). No separate
      // "gift added" confirmation panel — the gift lines in the cart already show it.
      if (plan.chooser && plan.chooser.options.length > 1) {
        var chosenId = plan.chooser.chosenKeepId;
        var current = plan.chooser.options.filter(function (o) { return o.keepId === chosenId; })[0] || plan.chooser.options[0];
        var alts = plan.chooser.options.filter(function (o) { return o.keepId !== chosenId; });
        html += '<div class="an45-gift-choice">';
        html += '<p class="an45-gift-choice__line">' + GIFT_SVG + '<span><strong>Your free gift:</strong> ' + esc(listShort(current.gifts)) + '</span></p>';
        html += '<details class="an45-gift-choice__details"><summary>Prefer a different free gift?</summary>';
        alts.forEach(function (o) {
          html += '<button type="button" class="an45-gift-choice__btn" data-an45-choice="' + esc(o.keepId) + '">Switch to: ' + esc(listShort(o.gifts)) + '</button>';
        });
        html += '</details></div>';
      }

      // Gifts the shopper removed, one line per GROUP rather than per gift — a decline
      // takes the whole offer's set (see computeGiftPlan), so listing them separately
      // would offer an "Add back" per row that all did the same thing.
      var shownBack = [];
      (plan.declined || []).forEach(function (h) {
        if (shownBack.indexOf(h) >= 0) return;
        var group = (plan.giftGroups && plan.giftGroups[h]) || [h];
        group = group.filter(function (g) { return (plan.declined || []).indexOf(g) >= 0; });
        if (!group.length) group = [h];
        group.forEach(function (g) { shownBack.push(g); });
        html += '<div class="an45-gift-nudge an45-gift-nudge--declined">';
        html += GIFT_SVG;
        html += '<span class="an45-gift-nudge__txt">Removed: ' + esc(listShort(group)) + '</span>';
        html += '<button type="button" class="an45-gift-nudge__add" data-an45-restore="' + esc(h) + '">Add back</button>';
        html += '</div>';
      });

      // Nudges (one product away from the next / a bigger gift).
      (plan.nudges || []).forEach(function (n) {
        var p = config.products[n.addHandle];
        html += '<div class="an45-gift-nudge">';
        html += GIFT_SVG;
        html += '<span class="an45-gift-nudge__txt">' + esc(n.text) + '</span>';
        if (p && p.id) html += '<button type="button" class="an45-gift-nudge__add" data-an45-add="' + esc(p.id) + '">Add</button>';
        html += '</div>';
      });

      /* "Gift colours and styles may vary" — LAST, and only when a gift is actually in
       * the cart (2026-09-21). Not on a nudge or a decline: those describe a gift the
       * shopper does not have, and a disclaimer about something you have not been given
       * reads as a warning about the offer itself.
       *
       * This is the moment of purchase, which is the one place the sentence has to
       * appear. The gifts are restocked as whatever the supplier has — the insulated
       * sipper arrived in 2026-09 as a cream/green product where every photo showed a
       * pink/blue ombre one — and since 2026-09-20 a wrong/missing-item complaint is a
       * formal process with a 2-day window (WEBSITE.md §14).
       *
       * The string comes from policies.gift_disclaimer in the config snippet, which is
       * its ONLY copy; set it to "" there to drop the line from every surface at once. */
      var disclaimer = (config.policies && config.policies.gift_disclaimer) || '';
      if (disclaimer && plan.gifts && plan.gifts.length) {
        html += '<p class="an45-gift-note">' + esc(disclaimer) + '</p>';
      }

      return html;
    }

    function paintRail() {
      var rail = document.getElementById('an45-gift-rail');
      if (!rail) return;
      paintCodes(); // same lifecycle as the rail: after every reconcile and drawer re-render
      var html = lastPlan ? buildRailHTML(lastPlan) : '';
      // Repaint only when the content changed, OR a drawer re-render swapped in the empty
      // server node (no children while we want some). Never compare rail.innerHTML.
      if (html === lastRailHTML && (html === '' || rail.childElementCount > 0)) return;
      lastRailHTML = html;
      rail.innerHTML = html;
      rail.hidden = !html;
      bindRail(rail);
    }

    /* ---------- discount codes: in-cart entry (policies, 2026-09-05) ----------
     * Codes entered here are applied to the CART (/cart/update.js `discount`), where
     * this engine can act on them before checkout — remove the gift a no-gift code
     * excludes, keep it for any other code. A code typed at Shopify's checkout is
     * invisible to the theme; the gift then simply stays free (every code combines
     * with product discounts), so the worst case is a gift given, never a gift charged.
     * Painted from the last AUTHORITATIVE cart, idempotently, like the rail. */
    function formatMoney(paise) {
      var fmt = config.money_format || 'Rs. {{amount}}'; // shop.money_format, from the config snippet
      var n = Number(paise || 0) / 100;
      return fmt
        .replace(/\{\{\s*amount_no_decimals[^}]*\}\}/g, String(Math.round(n)))
        .replace(/\{\{\s*amount[^}]*\}\}/g, n.toFixed(2));
    }
    // Rupees a code is currently taking off, from the cart's allocations (order-level
    // codes sit at cart level, product-level ones on the lines).
    function codeAmount(cart, code) {
      var sum = 0;
      var hit = function (a) { return a && a.type === 'discount_code' && String(a.title).toUpperCase() === String(code).toUpperCase(); };
      ((cart && cart.cart_level_discount_applications) || []).forEach(function (a) { if (hit(a)) sum += a.total_allocated_amount || 0; });
      ((cart && cart.items) || []).forEach(function (it) {
        (it.line_level_discount_allocations || []).forEach(function (al) { if (al && hit(al.discount_application)) sum += al.amount || 0; });
      });
      return sum;
    }
    function buildCodesHTML() {
      var cart = lastCart;
      if (!cart || !(cart.items || []).length) return '';
      var policies = config.policies || {};
      var entries = Array.isArray(cart.discount_codes)
        ? cart.discount_codes
        : lastCodes.map(function (c) { return { code: c, applicable: true }; });

      var html = '<details class="an45-codes"' + (entries.length ? ' open' : '') + '>';
      html += '<summary class="an45-codes__sum">' + TAG_SVG + '<span>Have a discount code?</span></summary>';
      html += '<form class="an45-codes__form" novalidate>';
      html += '<input class="an45-codes__in" type="text" name="an45-code" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="Enter code" aria-label="Discount code">';
      html += '<button type="submit" class="an45-codes__apply">Apply</button>';
      html += '</form>';
      if (entries.length) {
        html += '<ul class="an45-codes__list" role="list">';
        entries.forEach(function (e) {
          var code = String(e.code || '');
          var s = suppressingCode([code], policies);
          var label = s && s.rule.label ? s.rule.label + ' · ' : '';
          var status;
          if (e.applicable !== false) {
            var amt = codeAmount(cart, code);
            status = amt ? '−' + formatMoney(amt) : 'Applied';
          } else if (s && s.rule.min && (cart.items_subtotal_price || 0) < s.rule.min * 100) {
            // The one case the theme can explain: our own code, below its minimum.
            status = 'Applies from ' + formatMoney(s.rule.min * 100) + ' — add ' + formatMoney(s.rule.min * 100 - (cart.items_subtotal_price || 0)) + ' more';
          } else {
            status = 'Not applicable to this cart';
          }
          html += '<li class="an45-codes__item' + (e.applicable === false ? ' is-inactive' : '') + '">';
          html += '<span class="an45-codes__code">' + esc(code) + '</span>';
          html += '<span class="an45-codes__status">' + esc(label + status) + '</span>';
          html += '<button type="button" class="an45-codes__rm" data-an45-remove-code="' + esc(code) + '" aria-label="Remove code ' + esc(code) + '">&times;</button>';
          html += '</li>';
        });
        html += '</ul>';
      }
      if (lastPlan && lastPlan.suppressed) {
        html += '<p class="an45-codes__note">' + GIFT_SVG + '<span>' + esc(policies.no_gift_text || 'Free gifts don’t apply with this code.') + '</span></p>';
      }
      html += '<p class="an45-codes__msg" role="status" aria-live="polite"></p>';
      html += '</details>';
      return html;
    }
    function paintCodes() {
      var node = document.getElementById('an45-cart-codes');
      if (!node) return;
      // ⛔ OFF by default — the owner does not want a discount-code field in the cart
      // (2026-09-09, before it ever shipped: the whole policies layer was built on
      // 2026-09-05 but never pushed, so no customer has seen this). Turn it on with
      //   "policies": { "in_cart_codes": true }
      // in snippets/an45-gift-config.liquid; nothing else has to change.
      //
      // ⚠️ This hides the FIELD, not the policy. `no_gift_codes` still works: a code
      // applied by a /discount/<CODE> link (how the story-reward DMs send it) lands on
      // the cart, cart.js reports it, and computeGiftPlan still suppresses the gift. What
      // goes away is only the shopper's ability to type a code before checkout.
      if (!config.policies || config.policies.in_cart_codes !== true) {
        if (node.childElementCount) { node.innerHTML = ''; lastCodesHTML = null; }
        node.hidden = true;
        return;
      }
      var html = buildCodesHTML();
      if (html === lastCodesHTML && (html === '' || node.childElementCount > 0)) return;
      lastCodesHTML = html;
      node.innerHTML = html;
      node.hidden = !html;
      // Restore what the shopper was doing: a half-typed code and an opened panel survive
      // the drawer re-render that follows any cart change.
      var nextIn = node.querySelector('.an45-codes__in');
      if (nextIn && codesTyped) nextIn.value = codesTyped;
      var det = node.querySelector('details.an45-codes');
      if (det && codesOpen) det.open = true;
      bindCodes(node);
    }
    function bindCodes(node) {
      var det = node.querySelector('details.an45-codes');
      if (det) det.addEventListener('toggle', function () { codesOpen = det.open; });
      var inp = node.querySelector('.an45-codes__in');
      if (inp) inp.addEventListener('input', function () { codesTyped = inp.value; });
      var form = node.querySelector('.an45-codes__form');
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var inp = form.querySelector('.an45-codes__in');
          var code = ((inp && inp.value) || '').trim().toUpperCase();
          if (!code) { if (inp) inp.focus(); return; }
          var next = lastCodes.filter(function (c) { return String(c).toUpperCase() !== code; }).concat([code]);
          applyCodes(next, form, code);
        });
      }
      node.querySelectorAll('[data-an45-remove-code]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          btn.disabled = true;
          var code = String(btn.getAttribute('data-an45-remove-code') || '').toUpperCase();
          applyCodes(lastCodes.filter(function (c) { return String(c).toUpperCase() !== code; }), null, null);
        });
      });
    }
    function applyCodes(codes, form, added) {
      var node = document.getElementById('an45-cart-codes');
      var msg = node && node.querySelector('.an45-codes__msg');
      var btn = form && form.querySelector('.an45-codes__apply');
      if (btn) { btn.disabled = true; btn.textContent = 'Applying…'; }
      if (msg) msg.textContent = '';
      return setCodes(codes, true).then(function (cart) {
        if (!isFullCart(cart)) throw new Error('no cart in response');
        if (added) {
          var kept = (cart.discount_codes || []).some(function (d) { return String(d.code).toUpperCase() === added; });
          if (!kept) throw new Error('code rejected');
          codesTyped = '';
          var inp = form && form.querySelector('.an45-codes__in');
          if (inp) inp.value = '';
        }
        // Repaint the drawer from this response (totals, discount line) AND let our own
        // subscriber act on the new code: the optimistic paint hides an excluded gift in
        // the same frame, reconcile() then removes it for real.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'an45-cart-codes', cartData: cart, sections: cart.sections || null });
      }).catch(function () {
        if (msg) msg.textContent = 'That code couldn’t be added. Check it and try again.';
        if (btn) { btn.disabled = false; btn.textContent = 'Apply'; }
        node && node.querySelectorAll('[data-an45-remove-code]').forEach(function (b) { b.disabled = false; });
      });
    }

    function bindRail(rail) {
      rail.querySelectorAll('[data-an45-add]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          btn.disabled = true; btn.textContent = 'Adding…';
          // Pass the add response along as cartData so our own subscriber can paint the
          // newly-unlocked gift optimistically, same as the PDP add-to-cart path.
          addTriggerProduct(btn.getAttribute('data-an45-add'))
            .then(function (added) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'an45-gift-nudge', cartData: added });
            })
            .catch(function () { btn.disabled = false; btn.textContent = 'Add'; });
        });
      });
      rail.querySelectorAll('[data-an45-choice]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          btn.disabled = true;
          setChoiceAttr(btn.getAttribute('data-an45-choice')).then(function () {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'an45-gift-choice' });
          }).catch(function () { btn.disabled = false; });
        });
      });
    }

    /* ---------- toast ---------- */
    function maybeToast(plan) {
      if (prevGifts === null) return; // first run: don't celebrate a pre-existing cart
      var added = plan.gifts.filter(function (h) { return prevGifts.indexOf(h) < 0; });
      if (!added.length) return;
      showToast('Free gift unlocked: ' + listShort(added));
    }

    var toastTimer = null;
    function showToast(msg) {
      var t = document.getElementById('an45-gift-toast');
      if (!t) {
        t = document.createElement('div');
        t.id = 'an45-gift-toast';
        t.setAttribute('role', 'status');
        t.setAttribute('aria-live', 'polite');
        document.body.appendChild(t);
      }
      t.innerHTML = GIFT_SVG + '<span class="an45-gift-toast__txt">' + esc(msg) + '</span>';
      // reflow so the transition re-triggers even on rapid repeats
      void t.offsetWidth;
      t.classList.add('is-visible');
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 4200);
    }

    /* ---------- wiring ---------- */
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
      if (evt && evt.source === SELF) { paintRail(); return; } // our own refresh: repaint only
      paintOptimistic(evt);            // instant, paint-only
      reconcile(evt && evt.cartData);  // authoritative; ignores the hint unless it's a full cart
    });

    // Decline / restore a gift (policies, 2026-09-05). Delegated, so it works on gift rows
    // in the drawer, on /cart, and on the rail's "Add back" — all of which re-render.
    // The attribute write returns the full cart; reconcile from it directly (one repaint,
    // from the engine's own ops) rather than publishing a blind cartUpdate that would make
    // cart.js repaint once with the gift still there and once without.
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-an45-decline],[data-an45-restore]') : null;
      if (!t || t.disabled) return;
      e.preventDefault();
      var restoring = t.hasAttribute('data-an45-restore');
      var handle = t.getAttribute(restoring ? 'data-an45-restore' : 'data-an45-decline');
      if (!handle) return;
      // Restore must clear every handle in the group, not just the one clicked: the
      // attribute may hold a sibling (the shopper declined the mini shampoo, the group
      // withheld the conditioner too), and leaving one behind would keep the whole group
      // withheld with no way back.
      var group = (lastPlan && lastPlan.giftGroups && lastPlan.giftGroups[handle]) || [handle];
      var list = (lastDeclined || []).filter(function (h) {
        return h !== handle && !(restoring && group.indexOf(h) >= 0);
      });
      if (!restoring) list.push(handle);
      t.disabled = true;
      var row = restoring ? null : t.closest('tr');
      if (row) row.classList.add('an45-gift-line--pending-remove'); // gone on tap; the repaint deletes it
      setDeclined(list).then(function (cart) {
        if (isFullCart(cart)) paintOptimistic({ cartData: cart });
        reconcile(isFullCart(cart) ? cart : null);
      }).catch(function () {
        t.disabled = false;
        if (row) row.classList.remove('an45-gift-line--pending-remove');
      });
    });

    // Repaint the rail whenever the drawer subtree is re-rendered (idempotent).
    var drawerRoot = document.getElementById('CartDrawer');
    if (drawerRoot && typeof MutationObserver !== 'undefined') {
      var rafPending = false;
      new MutationObserver(function (mutations) {
        // Ignore our own writes into the rail and the codes panel — only react to real
        // drawer re-renders.
        var rail = document.getElementById('an45-gift-rail');
        var codes = document.getElementById('an45-cart-codes');
        var own = function (n) {
          return (rail && (n === rail || rail.contains(n))) || (codes && (n === codes || codes.contains(n)));
        };
        var external = (!rail && !codes) || mutations.some(function (m) { return !own(m.target); });
        if (!external || rafPending) return;
        rafPending = true;
        (window.requestAnimationFrame || setTimeout)(function () {
          rafPending = false;
          paintRail();
          // product-form.js publishes cartUpdate and THEN repaints the drawer, which wipes
          // the row we just drew. Re-draw it while the reconcile is still in flight.
          // applyOptimistic is idempotent, so the re-entrant mutation it triggers makes no
          // further changes and this settles after one extra pass.
          if (optimisticPlan && reconciling) applyOptimistic(optimisticPlan);
        });
      }).observe(drawerRoot, { childList: true, subtree: true });
    }

    reconcile(); // page load: self-heal (e.g. items added on another page)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGiftEngine);
  } else {
    initGiftEngine();
  }
})();
